/**
 * A list screen's query lives in the URL — shareable, and it survives a reload and the back button.
 * These convert between URLSearchParams and the typed query from a `defineListQuery()` schema
 * (@chukua/common), using the same conventions the API parses: repeated keys for multi-value filters.
 */

type SafeParseResult<Q> = { success: true; data: Q } | { success: false; error: { issues: { path: PropertyKey[] }[] } };

/** The slice of a Zod schema these helpers use — keeps the web app off a direct zod dependency. */
export interface ListQuerySchema<Q> {
  parse(input: unknown): Q;
  safeParse(input: unknown): SafeParseResult<Q>;
}

type ParamValue = string | string[];

function paramsToRecord(params: URLSearchParams): Record<string, ParamValue> {
  const record: Record<string, ParamValue> = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    record[key] = values.length > 1 ? values : values[0];
  }
  return record;
}

/** A hand-edited or stale URL (`?sort=bogus`) drops just the bad keys rather than the whole query. */
export function parseListParams<Q>(schema: ListQuerySchema<Q>, params: URLSearchParams): Q {
  const record = paramsToRecord(params);
  const result = schema.safeParse(record);
  if (result.success) return result.data;

  for (const issue of result.error.issues) delete record[String(issue.path[0])];
  const retried = schema.safeParse(record);
  return retried.success ? retried.data : schema.parse({});
}

function isDefault(value: unknown, defaultValue: unknown): boolean {
  return defaultValue !== undefined && String(value) === String(defaultValue);
}

/** Omits empty values, and values equal to `defaults` so the URL only shows what the user changed. */
export function toListParams(query: object, defaults: object = {}): URLSearchParams {
  const params = new URLSearchParams();
  const defaultValues = defaults as Record<string, unknown>;
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((item) => params.append(key, String(item)));
    else if (!isDefault(value, defaultValues[key])) params.set(key, String(value));
  }
  return params;
}
